import { Box, styled, useTheme } from '@mui/material';
import { useLocation, useParams } from 'react-router';
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import { useSingleTimesheet } from '../../queries/hooks/useSingleTimesheet';
import { formatWeekName } from '../../utils/formatters';
import { ContentWrapper } from '../layout/styled';
import { SingleTimesheetTable } from './components/SingleTimesheetTable/SingleTimesheetTable';
import { ROUTES } from '../../constants/routes';
import { useMemo } from 'react';
import { getWeeklyTimesheetWeekStart } from '../../utils/getWeeklyTimesheetWeekStart';
import { useAtomValue } from 'jotai';
import { isSmallScreenState, userState } from '../../state/UIState';
import { hasRole } from '../../utils/roles';
import { UserRole } from '../../types';

function SingleTimesheet() {
  const { id } = useParams();
  const location = useLocation();
  const isSmallScreen = useAtomValue(isSmallScreenState);
  const user = useAtomValue(userState);

  const isAllTimesheets = location.pathname.includes(ROUTES.ALL_TIMESHEETS);
  const isIssuedTimesheets = location.pathname.includes(ROUTES.ISSUED_TIMESHEETS);
  const { colors } = useTheme();
  const { data: singleTimesheet } = useSingleTimesheet({
    id: Number(id),
    enabled: id !== 'demo',
  });

  // The /all-timesheets/:id detail route is shared by the Submitter and Proxy contexts. When the
  // viewed sheet belongs to someone else and the viewer is a Proxy, the "All Timesheets" crumb must
  // point back to the Proxy list — otherwise a Proxy-only user lands on /all-timesheets which asks
  // the API for the Operating Partner dataset they don't hold (→ 403 → endless skeleton).
  const isProxiedView =
    !!singleTimesheet?.user?.id &&
    singleTimesheet.user.id !== user?.id &&
    hasRole(user, UserRole.PROXY);
  const allTimesheetsUrl = isProxiedView
    ? `/${ROUTES.PROXY_ALL_TIMESHEETS}`
    : `/${ROUTES.ALL_TIMESHEETS}`;

  const weekStart = useMemo(() => {
    if (!singleTimesheet) return '';
    return getWeeklyTimesheetWeekStart(singleTimesheet);
  }, [singleTimesheet]);

  const weekLength = useMemo(() => {
    if (!singleTimesheet) return 0;
    return singleTimesheet.weekDates.length + singleTimesheet.holidays.length;
  }, [singleTimesheet]);

  const week = formatWeekName(weekStart, weekLength);

  const breadcrumbTitle = isAllTimesheets
    ? 'All Timesheets'
    : isIssuedTimesheets
    ? 'Issued Timesheets'
    : 'Pending Timesheets';

  const breadcrumbUrl = isAllTimesheets
    ? allTimesheetsUrl
    : isIssuedTimesheets
    ? `/${ROUTES.ISSUED_TIMESHEETS}`
    : `/${ROUTES.PENDING_TIMESHEETS}`;
  return (
    <>
      <Wrapper isSmallScreen={isSmallScreen}>
        <Breadcrumbs
          breadcrumbs={[
            ...(!isAllTimesheets && !isSmallScreen
              ? [
                  {
                    title: 'All Timesheets',
                    color: colors.textAccent.default,
                    url: allTimesheetsUrl,
                  },
                ]
              : []),
            { title: breadcrumbTitle, url: breadcrumbUrl, color: colors.textAccent.default },
            { title: week || '' },
          ]}
        />
      </Wrapper>
      <ContentWrapper isSmallScreen={isSmallScreen}>
        <Box
          sx={{
            paddingBottom: isSmallScreen ? '40px' : '0',
          }}
        >
          <SingleTimesheetTable />
        </Box>
      </ContentWrapper>
    </>
  );
}

export default SingleTimesheet;

const Wrapper = styled('div')<{ isSmallScreen: boolean }>`
  padding: ${({ isSmallScreen }) => (isSmallScreen ? '48px 16px 0' : '28px 36px 0')};
`;
